import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Button from 'v2/components/Button';

import MoreActionsIcon from 'components/icons/MoreActionsIcon';

import {
    ESCAPE_KEY,
    ESTIMATED_MENU_HEIGHT_PIXELS,
    MENU_OFFSET_PIXELS,
    MENU_TEST_ID,
    TEST_ID
} from './constants';
import type { FunctionComponent, MenuPosition, Props } from './types';

import styles from './styles.module.scss';

const EquipmentRowActions: FunctionComponent<Props> = ({
    ariaLabel,
    className = '',
    deleteText,
    editText,
    onDelete,
    onEdit,
    testId = TEST_ID
}) => {
    const triggerRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);

    const closeMenu = useCallback(() => setMenuPosition(null), []);

    useEffect(() => {
        if (!menuPosition) {
            return undefined;
        }

        const handlePointerDown = (event: MouseEvent) => {
            const target = event.target as Node | null;

            if (
                target &&
                (triggerRef.current?.contains(target) || menuRef.current?.contains(target))
            ) {
                return;
            }

            closeMenu();
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === ESCAPE_KEY) {
                closeMenu();
            }
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        window.addEventListener('resize', closeMenu);
        window.addEventListener('scroll', closeMenu, true);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('resize', closeMenu);
            window.removeEventListener('scroll', closeMenu, true);
        };
    }, [closeMenu, menuPosition]);

    const handleToggle = useCallback(() => {
        if (menuPosition) {
            closeMenu();

            return;
        }

        const triggerBounds = triggerRef.current?.getBoundingClientRect();

        if (!triggerBounds) {
            return;
        }

        const hasRoomBelow =
            triggerBounds.bottom + ESTIMATED_MENU_HEIGHT_PIXELS < window.innerHeight;

        setMenuPosition({
            isAbove: !hasRoomBelow,
            right: window.innerWidth - triggerBounds.right,
            top: hasRoomBelow
                ? triggerBounds.bottom + MENU_OFFSET_PIXELS
                : triggerBounds.top - ESTIMATED_MENU_HEIGHT_PIXELS - MENU_OFFSET_PIXELS
        });
    }, [closeMenu, menuPosition]);

    const handleEditClick = useCallback(() => {
        closeMenu();
        onEdit();
    }, [closeMenu, onEdit]);

    const handleDeleteClick = useCallback(() => {
        closeMenu();
        onDelete();
    }, [closeMenu, onDelete]);

    return (
        <div className={`${className} ${styles.equipmentRowActions}`} ref={triggerRef}>
            <Button
                ariaExpanded={Boolean(menuPosition)}
                ariaLabel={ariaLabel}
                onClick={handleToggle}
                testId={testId}
            >
                <MoreActionsIcon className={styles.icon} />
            </Button>

            {menuPosition
                ? createPortal(
                      <div
                          className={styles.menu}
                          data-testid={MENU_TEST_ID}
                          ref={menuRef}
                          style={{ right: menuPosition.right, top: menuPosition.top }}
                      >
                          <Button className={styles.menuItem} onClick={handleEditClick}>
                              {editText}
                          </Button>

                          <Button
                              className={`${styles.menuItem} ${styles.deleteItem}`}
                              onClick={handleDeleteClick}
                          >
                              {deleteText}
                          </Button>
                      </div>,
                      document.body
                  )
                : null}
        </div>
    );
};

export default EquipmentRowActions;
